import { Placement } from "@floating-ui/react";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import get from "lodash/get";
import { ReactElement } from "react";
import {
  FieldErrors,
  FieldValues,
  Path,
  RegisterOptions,
  UseFormRegister,
} from "react-hook-form";

import { ErrorMessage } from "../catalyst/ErrorMessage";
import { Field } from "../catalyst/Field";
import { Label } from "../catalyst/Label";
import { Select } from "../catalyst/Select";
import { Tooltip } from "../Tooltip";

interface SelectOption {
  color?: string;
  label: string;
  value: string;
}

interface InputFieldGroupProps<FormType extends FieldValues> {
  errors: FieldErrors<FormType>;
  fieldName: Path<FormType>;
  label?: ReactElement | string;
  options: SelectOption[];
  register: UseFormRegister<FormType>;
  rules?: RegisterOptions<FormType, Path<FormType>>;
  tooltip?: {
    position: Placement;
    text: string;
  };
}

const SelectFieldGroup = <FormType extends FieldValues>({
  errors,
  fieldName,
  label,
  options,
  register,
  rules,
  tooltip,
}: InputFieldGroupProps<FormType>): ReactElement => {
  const errorObject = get(errors, fieldName);
  const errorMessage =
    typeof errorObject?.message === "string" ? errorObject.message : undefined;

  const { onChange, ...registerProps } = register(fieldName, rules);

  const getOptionColor = (value: string): string | undefined => {
    const option = options.find((opt) => opt.value === value);
    return option?.color;
  };

  return (
    <Field>
      {label && typeof label === "string" ? (
        <Label className="flex items-center">
          {label}
          {tooltip && (
            <span className="ml-1">
              <Tooltip content={tooltip.text} position={tooltip.position}>
                <InformationCircleIcon className="h-4 w-4 text-gray-500 hover:text-gray-700" />
              </Tooltip>
            </span>
          )}
        </Label>
      ) : (
        label
      )}

      <Select
        {...registerProps}
        invalid={!!errorMessage}
        onChange={(e) => {
          onChange(e);

          const color = getOptionColor(e.target.value);
          if (color) {
            e.target.style.color = color;
          } else {
            e.target.style.color = "";
          }
        }}
      >
        {options.map(({ color, label, value }) => (
          <option key={value} style={{ color }} value={value}>
            {label}
          </option>
        ))}
      </Select>
      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </Field>
  );
};

export default SelectFieldGroup;
